import { useState } from "react";
import { compact, dayOfMonth, money } from "../format.ts";
import type { Lang } from "../i18n.ts";

interface Props {
  days: { date: string; expense: number }[];
  lang: Lang;
}

const H = 120;
const PAD_TOP = 18;

/** Расходы по дням: одна серия столбцов, подсказка по нажатию, нулевые дни — тонкая метка у оси. */
export function DailyChart({ days, lang }: Props) {
  const [active, setActive] = useState<number | null>(null);
  const max = Math.max(...days.map((d) => d.expense), 1);
  const n = Math.max(days.length, 1);
  const slot = 100 / n;
  const barW = Math.max(slot * 0.62, 0.8);
  const shown = active ?? days.reduce((best, d, i) => (d.expense > (days[best]?.expense ?? -1) ? i : best), 0);
  const tip = days[shown];
  const avg = days.reduce((s, d) => s + d.expense, 0) / n;

  return (
    <div className="daily">
      <div className="daily-tip" aria-live="polite">
        {tip && (
          <>
            <b>{money(tip.expense, "UZS", lang)}</b>
            <span>
              {" "}
              · {tip.date.slice(8, 10)}.{tip.date.slice(5, 7)}
            </span>
          </>
        )}
      </div>
      <svg
        viewBox={`0 0 100 ${H}`}
        preserveAspectRatio="none"
        className="daily-svg"
        role="img"
        aria-label={days.map((d) => `${dayOfMonth(d.date)}: ${Math.round(d.expense)}`).join(", ")}
      >
        {/* Средний расход в день — пунктир */}
        <line
          x1="0"
          x2="100"
          y1={H - (avg / max) * (H - PAD_TOP)}
          y2={H - (avg / max) * (H - PAD_TOP)}
          className="daily-avg"
          vectorEffect="non-scaling-stroke"
        />
        {days.map((d, i) => {
          const h = d.expense > 0 ? Math.max((d.expense / max) * (H - PAD_TOP), 2) : 1;
          const x = i * slot + (slot - barW) / 2;
          return (
            <g key={d.date} onClick={() => setActive(i)} onMouseEnter={() => setActive(i)}>
              {/* Зона нажатия шире столбца */}
              <rect x={i * slot} y={0} width={slot} height={H} fill="transparent" />
              <rect
                x={x}
                y={H - h}
                width={barW}
                height={h}
                rx={Math.min(barW / 2, 1.2)}
                className={i === shown ? "daily-bar active" : d.expense > 0 ? "daily-bar" : "daily-bar zero"}
              />
            </g>
          );
        })}
      </svg>
      <div className="daily-axis">
        <span>{dayOfMonth(days[0]?.date ?? "01")}</span>
        <span className="daily-avg-label">⌀ {compact(avg, lang)}</span>
        <span>{dayOfMonth(days.at(-1)?.date ?? "01")}</span>
      </div>
    </div>
  );
}
